// Stepper — horizontal flow indicator per preview/stepper.html.
// Three node states: done (filled primary + ✓), active (white center
// + primary ring + pulse halo), pending (white + ink-mute ring).
// Connectors mirror the done/pending tokens.
//
// Derived state: index < activeIndex → done · === active · > pending.
// Pass activeIndex === steps.length to mark every step complete (the
// "all done" terminal render).

export type StepperProps = {
  steps: ReadonlyArray<string>;
  activeIndex: number;
};

export function Stepper({ steps, activeIndex }: StepperProps) {
  return (
    <ol className="flex items-start gap-0 w-full py-1.5">
      {steps.map((label, i) => {
        const state: 'done' | 'active' | 'pending' =
          i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'pending';
        return (
          <li key={label} className="flex items-start flex-1 first:flex-[0_0_auto] last:flex-[0_0_auto]">
            {i > 0 ? <Connector done={i <= activeIndex} /> : null}
            <Node label={label} index={i} state={state} />
          </li>
        );
      })}
    </ol>
  );
}

function Node({
  label,
  index,
  state,
}: {
  label: string;
  index: number;
  state: 'done' | 'active' | 'pending';
}) {
  const iconBase =
    'w-8 h-8 rounded-full inline-flex items-center justify-center font-sans font-bold text-[13px] leading-none';
  const iconCls =
    state === 'done'
      ? `${iconBase} bg-primary text-white`
      : state === 'active'
        ? `${iconBase} bg-card text-primary border-2 border-primary`
        : `${iconBase} bg-card text-foreground-mute border-2 border-border-strong`;
  const labelCls =
    state === 'done'
      ? 'text-foreground font-semibold'
      : state === 'active'
        ? 'text-primary font-bold'
        : 'text-foreground-mute font-semibold';
  const haloStyle =
    state === 'active' ? { boxShadow: '0 0 0 4px var(--pulse-primary)' } : undefined;
  const glyph = state === 'done' ? '✓' : String(index + 1);
  return (
    <div className="flex flex-col items-center gap-2 min-w-[80px]">
      <span className={iconCls} style={haloStyle} aria-current={state === 'active' ? 'step' : undefined}>
        {glyph}
      </span>
      <span className={`text-xs ${labelCls}`}>{label}</span>
    </div>
  );
}

function Connector({ done }: { done: boolean }) {
  const cls = done ? 'bg-primary' : 'bg-border';
  return <span className={`flex-1 h-0.5 mt-[15px] mx-1 ${cls}`} aria-hidden />;
}
